import type { LocationQuery, LocationQueryRaw, Router } from 'vue-router';
import { TRACKING_PARAM_STORAGE_KEY } from '@/services/analytics/storage';
import { readCookieConsentFromDocument, type ICookieConsent } from '@/utils/cookieConsent.utils';

const TRACKING_KEYS = ['gclid', '_gl', 'gbraid', 'wbraid'];

export default function setupTrackingParamCarryForward(router: Router): void {
  router.beforeEach((to, from) => {
    const consent = readCookieConsentFromDocument();
    const incoming = pickTrackingParams(to.query);
    const stored = readStoredTrackingParams(consent);
    // Keep the incoming campaign in the URL until a choice; do not persist it before consent.
    const combined: LocationQueryRaw = { ...stored, ...pickTrackingParams(from.query), ...incoming };

    writeStoredTrackingParams(pickConsentedParams(combined, consent));

    if (Object.keys(combined).length === 0) {
      return;
    }

    const missing = getMissingParams(to.query, combined);
    if (Object.keys(missing).length === 0) {
      return;
    }

    return {
      path: to.path,
      query: {
        ...to.query,
        ...missing,
      },
      hash: to.hash,
      // A push must keep its previous page in history; Vue Router also preserves
      // an explicitly requested replace. Only enrich the current history entry
      // in place on initial load or Back/Forward, when the URL has already moved.
      ...(to.fullPath === router.options.history.location ? { replace: true } : {}),
    };
  });
}

function pickTrackingParams(query: LocationQuery): LocationQueryRaw {
  return Object.fromEntries(
    Object.entries(query).filter(([key, value]) => isTrackingParam(key) && value != null),
  ) as LocationQueryRaw;
}

function getMissingParams(query: LocationQuery, stored: LocationQueryRaw): LocationQueryRaw {
  const missing: LocationQueryRaw = {};

  for (const [key, value] of Object.entries(stored)) {
    if (query[key] == null) {
      missing[key] = value;
    }
  }

  return missing;
}

function isTrackingParam(key: string): boolean {
  return key.startsWith('utm_') || TRACKING_KEYS.includes(key);
}

function pickConsentedParams(params: LocationQueryRaw, consent: ICookieConsent | null): LocationQueryRaw {
  return Object.fromEntries(
    Object.entries(params).filter(([key]) => (key.startsWith('utm_') ? consent?.analytics : consent?.marketing)),
  );
}

function readStoredTrackingParams(consent: ICookieConsent | null): LocationQueryRaw {
  if (typeof window === 'undefined') {
    return {};
  }

  try {
    const raw = window.sessionStorage.getItem(TRACKING_PARAM_STORAGE_KEY);
    if (!raw) {
      return {};
    }

    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return {};
    }

    return pickConsentedParams(pickTrackingParams(parsed as LocationQuery), consent);
  } catch {
    return {};
  }
}

function writeStoredTrackingParams(params: LocationQueryRaw): void {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    if (Object.keys(params).length) {
      window.sessionStorage.setItem(TRACKING_PARAM_STORAGE_KEY, JSON.stringify(params));
    } else {
      window.sessionStorage.removeItem(TRACKING_PARAM_STORAGE_KEY);
    }
  } catch {
    // Ignore storage errors.
  }
}
